import { Apple, Binoculars, Map, Notebook, ShieldUser } from 'lucide-preact';
import { ComponentProps } from 'preact';
import { ReactNode } from 'preact/compat';

import { ExperienceOption } from '@/redux/slices/user/types';

interface PersonaSelectorProps extends Omit<ComponentProps<'div'>, 'value' | 'onChange'> {
  value: ExperienceOption | null;
  onChange: (value: ExperienceOption) => void;
}

const OPTIONS: { id: ExperienceOption; label: string; description: string; icon: ReactNode }[] = [
  {
    id: 'zooGoer',
    label: 'Zoo Goer',
    description: 'Here to stay connected with my local zoo and the animals I visit.',
    icon: (
      <div className="flex size-12 items-center justify-center rounded-full bg-[#76AEA9] text-white">
        <Map className="preflight preflight-svg size-7" />
      </div>
    ),
  },
  {
    id: 'educator',
    label: 'Teacher',
    description: 'Here to stream animals & bring real animal behavior into my lessons.',
    icon: (
      <div className="flex size-12 items-center justify-center rounded-full bg-[#76A6F2] text-white">
        <Apple className="preflight preflight-svg size-7" />
      </div>
    ),
  },
  {
    id: 'parent',
    label: 'Parent',
    description: 'Here to explore animals with my family in a safe, educational way.',
    icon: (
      <div className="flex size-12 items-center justify-center rounded-full bg-[#C930A7] text-white">
        <ShieldUser className="preflight preflight-svg size-7" />
      </div>
    ),
  },
  {
    id: 'animalLover',
    label: 'Animal Lover',
    description: 'Here to watch, connect, and relax with animals & nature.',
    icon: (
      <div className="flex size-12 items-center justify-center rounded-full bg-primary-green text-white">
        <Binoculars className="preflight preflight-svg size-7" />
      </div>
    ),
  },
  {
    id: 'researcher',
    label: 'Researcher',
    description: 'Here to observe and gather insights for my studies or fieldwork.',
    icon: (
      <div className="flex size-12 items-center justify-center rounded-full bg-primary-yellow text-white">
        <Notebook className="preflight preflight-svg size-7" />
      </div>
    ),
  },
];

const PersonaSelector = ({ value, onChange, ...props }: PersonaSelectorProps) => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...props}>
      {OPTIONS.map((opt) => {
        const isSelected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(opt.id)}
            className="flex w-full cursor-pointer items-center rounded-lg border border-solid border-gray-300 bg-white p-4 transition-colors hover:bg-[#FEFFF9] aria-checked:border-primary-green aria-checked:bg-[#FEFFF9]"
          >
            <div className="mr-3 shrink-0">{opt.icon}</div>
            <div className="flex flex-col text-left">
              <span className="text-base font-semibold text-[#162B1B]">{opt.label}</span>
              <span className="text-sm text-gray-500">{opt.description}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default PersonaSelector;
